import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
} from 'reactstrap';
import { CardHeader, Button } from '@cordata/buffet';
import { useHistory } from 'react-router-dom';
import { parse, distanceInWordsStrict } from 'date-fns';
import {
  formatDate,
  getFullName,
} from '~/utils';
import { MatchStatus } from '~/utils/const/identity';
import DataPoint from './DataPoint';
import ManualMRNMatchModal from './ManualMRNMatchModal';

interface SacwisDataCardProps {
  matchStatusId: string;
  data: any;
  fetchData: Function;
}

const SacwisDataCard = ({
  matchStatusId,
  data,
  fetchData,
}: SacwisDataCardProps) => {
  const history = useHistory();
  const [isManualMatchOpen, setIsManualMatchOpen] = useState(false);

  const handleManualMatchPrep = () => {
    setIsManualMatchOpen(true);
  };

  const afterManualMrnMatchSubmission = () => {
    if (matchStatusId !== MatchStatus.Unmatched) {
      fetchData();
    } else {
      history.push({
        pathname: '/admin/identity-matches/match-actions',
        search: '?expand=Unmatched',
        state: { refreshModel: Math.random() },
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          color="success"
          inverse
          action={
            <Button
              size="sm"
              outline
              color="light"
              icon="plus"
              onClick={handleManualMatchPrep}
            >
              Manual MRN Match
            </Button>
          }
        >
          <div className="d-flex justify-content-between">
            SACWIS
          </div>
        </CardHeader>

        <CardBody>
          <div className="d-flex flex-column">
            <Row>
              <Col sm>
                <DataPoint
                  label={getFullName(data.attributes.firstName, data.attributes.lastName)}
                />

                <DataPoint
                  label="Age"
                  value={
                    data &&
                    distanceInWordsStrict(
                      parse(data.attributes.birthDate),
                      new Date(),
                      { unit: 'Y' },
                    )
                  }
                />

                <DataPoint label={data.attributes.gender} />

                <DataPoint
                  label="Alias"
                  value={data.attributes.alias1}
                />
              </Col>

              <Col sm>
                <DataPoint
                  label="Date of birth"
                  value={data && formatDate(data.attributes.birthDate)}
                />

                <DataPoint
                  label="Person ID"
                  value={data.attributes.sacwisPersonId}
                />

                <DataPoint label="Case ID" value={data.attributes.caseId} />

                <DataPoint label="County" value={data.attributes.agencyCounty} />

                <DataPoint label="Race" value={data.attributes.race} />

                <DataPoint label="Ethnicity" value={data.attributes.ethnicity} />
              </Col>
            </Row>

            <section>
              <h5>Case Information</h5>

              <div className=" ml-4">
                <DataPoint label="Case name" value={data.attributes.caseName} />

                <DataPoint
                  label="Case open date"
                  value={formatDate(data.attributes.openDate)}
                />
              </div>
            </section>

            <section className="my-1">
              <h5>Legal Status </h5>

              <div className=" ml-4">
                <DataPoint
                  label="Current custody begin"
                  value={formatDate(data.attributes.custodyStartDate)}
                />

                <DataPoint
                  label="Lifetime custody episodes"
                  value={data.attributes.totalCustodyEpisodes}
                />

                <DataPoint
                  label="Current placement"
                  value={
                    <span>
                      {data.attributes.providerName} <br />
                      {formatDate(data.attributes.placementBegin)}
                    </span>
                  }
                />
              </div>
            </section>

            <section>
              <h5>Placement</h5>

              <div className=" ml-4">
                <DataPoint label="Type" value={data.attributes.placementType} />

                <DataPoint
                  label="Placement agency"
                  value={
                    <span>
                      {data.attributes.providerAgency}
                      <br />
                      {data.attributes.providerAgencyContact}
                    </span>
                  }
                />

                <DataPoint
                  label="Current placement"
                  value={data.attributes.providerName}
                />

                <DataPoint
                  label="Placement leave date"
                  value={formatDate(data.attributes.leaveDateDt)}
                />
              </div>
            </section>
          </div>
        </CardBody>
      </Card>

      <ManualMRNMatchModal
        data={data}
        isOpen={isManualMatchOpen}
        setIsOpen={setIsManualMatchOpen}
        afterManualMrnMatchSubmission={afterManualMrnMatchSubmission}
      />
    </>
  );
};

export default SacwisDataCard;
